import { useState, type FormEvent } from "react";
import { AnimatePresence, motion, useReducedMotion } from "motion/react";
import { Anchor, ArrowRight, UserPlus, X } from "@phosphor-icons/react";
import { gameCopy } from "../../content";
import { MAX_CREW, useGame } from "../../game/GameContext";
import { Waypoint } from "../game/Waypoint";

const crewCopy = gameCopy.stations.crew;

export function CrewMuster() {
  const reduce = useReducedMotion();
  const { crew, addCrew, removeCrew, stamp, hasStamp } = useGame();
  const [name, setName] = useState("");
  const full = crew.length >= MAX_CREW;

  const onSubmit = (e: FormEvent) => {
    e.preventDefault();
    const trimmed = name.trim();
    if (!trimmed || full) return;
    addCrew(trimmed);
    stamp("crew");
    setName("");
  };

  return (
    <section id="crew" className="bg-paper-deep py-24 md:py-36">
      <div className="mx-auto w-full max-w-[1400px] px-5 md:px-10">
        <Waypoint station="crew" title={crewCopy.title} sub={crewCopy.sub} />

        <div className="mt-12 grid overflow-hidden border-2 border-ink bg-foam shadow-lift lg:grid-cols-[minmax(20rem,0.78fr)_minmax(34rem,1.22fr)]">
          {/* A compact dock-office manifest keeps the action close to the roster. */}
          <div className="flex min-h-[35rem] flex-col p-6 sm:p-8 lg:p-10">
            <div className="flex items-center justify-between gap-4 border-b border-line pb-5">
              <div className="flex items-center gap-2 font-mono text-[10px] tracking-[0.14em] text-signal-deep uppercase">
                <Anchor className="size-4" weight="bold" aria-hidden />
                Boarding manifest
              </div>
              <span className="font-mono text-xs font-semibold text-ink">{crew.length} / {MAX_CREW} berths</span>
            </div>

            <form onSubmit={onSubmit} noValidate>
              <label htmlFor="crew-name" className="mt-7 block text-sm font-semibold text-ink">
                {crewCopy.label}
              </label>
              <div className="mt-2">
                <input
                  id="crew-name"
                  type="text"
                  value={name}
                  maxLength={24}
                  disabled={full}
                  onChange={(e) => setName(e.target.value)}
                  placeholder={crewCopy.placeholder}
                  className="h-13 w-full border-0 border-b-2 border-ink bg-paper px-4 text-ink outline-none transition-colors placeholder:text-muted focus:border-signal-deep disabled:opacity-50"
                />
                <button
                  type="submit"
                  disabled={full || !name.trim()}
                  className="mt-3 inline-flex h-12 w-full cursor-pointer items-center justify-between rounded-full bg-ink px-6 text-[0.95rem] font-semibold whitespace-nowrap text-foam transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:bg-ink-soft active:translate-y-0 active:scale-[0.98] disabled:cursor-not-allowed disabled:opacity-40 disabled:hover:translate-y-0 motion-reduce:hover:translate-y-0 motion-reduce:active:scale-100"
                >
                  <span className="flex items-center gap-2"><UserPlus className="size-4" weight="bold" aria-hidden />{crewCopy.add}</span>
                  <ArrowRight className="size-4" weight="bold" aria-hidden />
                </button>
              </div>
              <p className="mt-3 font-mono text-[11px] leading-relaxed text-muted">
                {full ? crewCopy.full : crewCopy.note}
              </p>
            </form>

            {/* Named berths replace the old coded boat diagram. */}
            <div className="mt-8 flex items-center justify-between">
              <p className="font-mono text-[10px] tracking-[0.14em] text-muted uppercase">Crew list</p>
              <p className="font-hand text-lg text-ink-soft">names for now</p>
            </div>
            <ul aria-live="polite" className="mt-3 grid gap-2">
              <AnimatePresence>
                {crew.map((member) => (
                  <motion.li
                    key={member}
                    initial={reduce ? false : { opacity: 0, scale: 0.9 }}
                    animate={{ opacity: 1, scale: 1 }}
                    exit={reduce ? undefined : { opacity: 0, scale: 0.9 }}
                    transition={{ duration: 0.25 }}
                    className="flex min-h-12 items-center gap-3 border border-line bg-paper px-3 py-2 font-semibold text-ink"
                  >
                    <span className="flex size-7 shrink-0 items-center justify-center rounded-full bg-signal font-mono text-[10px] font-semibold text-ink">
                      {String(crew.indexOf(member) + 1).padStart(2, "0")}
                    </span>
                    <span className="min-w-0 flex-1 truncate">{member}</span>
                    <button
                      type="button"
                      onClick={() => removeCrew(member)}
                      aria-label={`${crewCopy.remove} ${member}`}
                      className="flex size-7 cursor-pointer items-center justify-center rounded-full text-muted transition-colors hover:bg-paper-deep hover:text-ink"
                    >
                      <X weight="bold" className="size-3.5" aria-hidden />
                    </button>
                  </motion.li>
                ))}
              </AnimatePresence>
              {Array.from({ length: MAX_CREW - crew.length }, (_, index) => (
                <li key={`open-${index}`} className="flex min-h-12 items-center gap-3 border border-dashed border-line px-3 py-2 text-muted">
                  <span className="flex size-7 shrink-0 items-center justify-center rounded-full border border-line font-mono text-[10px]">
                    {String(crew.length + index + 1).padStart(2, "0")}
                  </span>
                  <span className="font-mono text-[10px] tracking-[0.1em] uppercase">Open berth</span>
                </li>
              ))}
            </ul>

            {crew.length === 0 && !hasStamp("crew") && (
              <div className="mt-auto flex flex-wrap items-center gap-4 pt-8">
                <button
                  type="button"
                  onClick={() => stamp("crew")}
                  className="inline-flex h-11 cursor-pointer items-center rounded-full border border-ink px-5 text-sm font-semibold text-ink transition-transform duration-200 ease-out hover:-translate-y-0.5 hover:bg-ink/5 active:translate-y-0 motion-reduce:hover:translate-y-0"
                >
                  {crewCopy.soloButton}
                </button>
                <p className="hand-note text-ink-soft">{crewCopy.solo}</p>
              </div>
            )}
          </div>

          {/* The community is shown as a real game-world scene, not a coded icon. */}
          <figure className="group relative min-h-[28rem] overflow-hidden border-t-2 border-ink lg:min-h-[35rem] lg:border-t-0 lg:border-l-2">
            <img
              src="/page-assets/community-table.jpg"
              alt="Builders and otters sharing their work around a harbour table at dusk"
              className="absolute inset-0 size-full object-cover transition-transform duration-700 ease-out group-hover:scale-[1.015] motion-reduce:transition-none motion-reduce:group-hover:scale-100"
            />
            <div className="absolute inset-0 bg-gradient-to-t from-ink/88 via-ink/5 to-transparent" aria-hidden />
            <figcaption className="absolute inset-x-5 bottom-5 border border-foam/35 bg-ink/88 p-5 text-foam backdrop-blur-sm sm:inset-x-7 sm:bottom-7 sm:p-6">
              <p className="font-mono text-[9px] tracking-[0.17em] text-signal uppercase">The table ahead</p>
              <p className="mt-2 text-[clamp(1.5rem,3vw,2.2rem)] leading-tight font-bold tracking-[-0.02em]">
                {crew.length > 0 ? `${crew.join(", ")} are on the manifest.` : "Your table is waiting."}
              </p>
              <p className="mt-2 max-w-xl text-sm leading-relaxed text-night-text">
                Six builders. Real work on the table. One useful ask each.
              </p>
            </figcaption>
            <span className="absolute top-5 right-5 border border-ink bg-foam px-3 py-2 font-mono text-[9px] tracking-[0.12em] text-ink uppercase shadow-card sm:top-7 sm:right-7">
              Harbour table · 06 seats
            </span>
          </figure>
        </div>
      </div>
    </section>
  );
}
